import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/pages/home/context/LangContext";
import { submitLead } from "@/lib/supabase/leads";

export const SUPPORT_EMAIL = "support@diyafa.tech";

// Only links that lead somewhere real. Add Blog / Careers / Status etc.
// back once those pages exist.
type FooterLink = { en: string; ar: string } & (
  | { section: string }
  | { route: string }
  | { href: string }
);

const footerColumns: Array<{ en: string; ar: string; links: FooterLink[] }> = [
  { en: "Product", ar: "المنتج", links: [
    { en: "Features", ar: "المميزات", section: "features" },
    { en: "Pricing",  ar: "الأسعار",  section: "pricing" },
  ] },
  { en: "Support", ar: "الدعم", links: [
    { en: "Contact",       ar: "تواصل معنا",        section: "contact-form" },
    { en: "Email support", ar: "راسل الدعم",        href: `mailto:${SUPPORT_EMAIL}` },
  ] },
  { en: "Legal", ar: "القانونية", links: [
    { en: "Privacy Policy",   ar: "سياسة الخصوصية", route: "/privacy" },
    { en: "Terms of Service", ar: "شروط الخدمة",    route: "/terms" },
  ] },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscribeError, setSubscribeError] = useState(false);
  const { isAr, t } = useLang();
  const navigate = useNavigate();

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setSubscribeError(false);
    try {
      await submitLead({ kind: "newsletter", email, lang: isAr ? "ar" : "en" });
      setSubmitted(true);
      setEmail("");
    } catch {
      setSubscribeError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  };

  const linkClass = `text-sm text-white/60 transition-colors cursor-pointer hover:text-gold-light ${isAr ? "font-tajawal" : ""}`;

  return (
    <footer id="contact" dir={isAr ? "rtl" : "ltr"} className="bg-[#78350F]">
      {/* CTA Banner */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div
            className="rounded-3xl p-10 text-center relative overflow-hidden"
            style={{
              backgroundImage: "url('https://readdy.ai/api/search-image?query=elegant%20Omani%20hotel%20exterior%20at%20golden%20hour%2C%20warm%20amber%20sunset%20light%2C%20traditional%20Arabic%20architecture%2C%20palm%20trees%2C%20luxury%20hospitality%2C%20wide%20angle%20photography&width=1200&height=500&seq=footer-cta-bg&orientation=landscape')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#78350F]/90 via-[#92400E]/80 to-[#78350F]/90 rounded-3xl"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 border border-gold/30 mb-5">
                <i className="ri-door-open-line text-gold-light text-sm"></i>
                <span className={`text-[13px] font-semibold text-gold-light ${isAr ? "font-tajawal" : ""}`}>
                  {t("Start Your Journey", "ابدأ رحلتك")}
                </span>
              </div>
              <h2 className={`font-bold text-3xl md:text-4xl mb-4 text-white ${isAr ? "font-tajawal" : "font-inter"}`}>
                {t("Start Your Diyafa Journey Today", "ابدأ رحلتك مع ضيافة اليوم")}
              </h2>
              <p className={`text-base mb-8 max-w-lg mx-auto text-white/70 ${isAr ? "font-tajawal" : ""}`}>
                {t("14 days free. No credit card. Cancel anytime.", "14 يوماً مجاناً. بلا بطاقة ائتمان. إلغاء في أي وقت.")}
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => navigate("/register")}
                  className={`px-8 py-3.5 rounded-xl font-semibold text-ink bg-gold transition-all cursor-pointer hover:bg-gold-dark whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}
                >
                  {t("Start Free Trial", "ابدأ مجاناً")}
                </button>
                <button
                  onClick={() => scrollTo("contact-form")}
                  className={`px-8 py-3.5 rounded-xl font-semibold text-white border border-white/30 bg-white/10 transition-all cursor-pointer hover:bg-white/20 whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}
                >
                  {t("Talk to Sales", "تحدث مع المبيعات")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Brand + Newsletter */}
          <div className="md:col-span-2">
            <div className={`flex items-center gap-3 mb-4 ${isAr ? "flex-row-reverse" : ""}`}>
              <div className="w-10 h-10 rounded-xl bg-gold/25 flex items-center justify-center">
                <i className="ri-door-open-line text-gold-light text-lg"></i>
              </div>
              <div>
                <span className="font-tajawal font-bold text-xl text-white">ضيافة</span>
                <span className="font-inter font-bold text-xl ml-1 text-white">Diyafa</span>
              </div>
            </div>
            <p className={`text-sm leading-relaxed mb-2 text-white/60 ${isAr ? "font-tajawal" : ""}`}>
              {t(
                "The all-in-one hospitality management platform for hotels and car rental businesses in Oman.",
                "المنصة الشاملة لإدارة الضيافة للفنادق وشركات تأجير السيارات في عُمان."
              )}
            </p>
            <p className={`text-xs text-gold-light/60 mb-5 ${isAr ? "font-tajawal" : ""}`}>
              {t("Part of the HISN (حِصن) ecosystem", "جزء من منظومة حِصن")}
            </p>
            {submitted ? (
              <p className={`text-sm text-gold-light font-medium ${isAr ? "font-tajawal" : ""}`}>
                {t("✓ Thanks for subscribing!", "✓ شكراً على اشتراكك!")}
              </p>
            ) : (
              <form className={`flex gap-2 ${isAr ? "flex-row-reverse" : ""}`} onSubmit={handleSubscribe}>
                <input
                  type="email"
                  name="email"
                  placeholder={t("Your email", "بريدك الإلكتروني")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-sm text-white border border-white/15 placeholder-white/30 outline-none focus:border-gold/50 bg-white/8 ${isAr ? "font-tajawal text-right" : ""}`}
                  style={{ background: "rgba(255,255,255,0.08)" }}
                  required
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-4 py-2.5 rounded-lg text-sm font-semibold text-ink bg-gold cursor-pointer hover:bg-gold-dark whitespace-nowrap disabled:opacity-60 ${isAr ? "font-tajawal" : ""}`}
                >
                  {submitting ? "..." : t("Subscribe", "اشترك")}
                </button>
              </form>
            )}
            {subscribeError && (
              <p role="alert" className={`mt-2 text-xs text-red-200 ${isAr ? "font-tajawal" : ""}`}>
                {t("Couldn't subscribe — please try again.", "تعذّر الاشتراك — حاول مرة أخرى.")}
              </p>
            )}
          </div>

          {/* Link columns */}
          {footerColumns.map((col) => (
            <div key={col.en}>
              <h4 className={`font-bold text-sm mb-4 text-gold-light ${isAr ? "font-tajawal" : ""}`}>{t(col.en, col.ar)}</h4>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.en}>
                    {"href" in link ? (
                      <a href={link.href} className={linkClass}>{t(link.en, link.ar)}</a>
                    ) : (
                      <button
                        onClick={() => ("route" in link ? navigate(link.route) : scrollTo(link.section))}
                        className={linkClass}
                      >
                        {t(link.en, link.ar)}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className={`mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 ${isAr ? "sm:flex-row-reverse" : ""}`}>
          <p className={`text-sm text-white/40 ${isAr ? "font-tajawal" : ""}`}>
            {t("© 2026 Diyafa. All rights reserved.", "© 2026 ضيافة. جميع الحقوق محفوظة.")}
          </p>
          <div className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
            {/* Build version — single source of truth from /version.json, injected
             *  at build time by vite.config.ts. Hovering shows commit + build time
             *  for bug reports / CI traceability. Always LTR so the SemVer string
             *  isn't mirrored in Arabic layouts. */}
            <span
              className="inline-flex items-center gap-1 text-[11px] text-white/40 font-mono px-2 py-0.5 rounded border border-white/10 bg-white/[0.03]"
              dir="ltr"
              title={`Build ${__APP_VERSION_META__.commit} · ${__APP_VERSION_META__.builtAt}`}
            >
              <i className="ri-git-commit-line text-gold-light" />
              <span>v{__APP_VERSION__}</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
